/**
 * One-time "this is new" pop-ups (Coleman, 2026-10-10).
 *
 * Each intro shows once per account, on the next open after it starts, to
 * coaches who were already here (newer coaches get the tour instead). Seen
 * state is a user_data row so it doesn't repeat on another device. An intro
 * needs its feature switched on (`requires`, a featureFlags key); if the
 * switch is off it waits. It stops being offered `days` after it starts, so
 * someone back from a long break isn't met with old news.
 *
 * `copy(ctx)` picks the words for where the coach is: ctx.native (iPhone app)
 * and ctx.dms (direct messages available here). Return null to not show.
 *
 * Writing one: plain words, what it is and what to do with it, no hype, no
 * em dashes. `action` is what the primary button does (handled in BAMFull).
 */
import { FILM_STUDIO_OPENS } from "../utils/featureFlags";

export const SEEN_INTROS_KEY = "seen_intros";

export const FEATURE_INTROS = [
  {
    id: "coach-map-dms-2026-10",
    startsAt: "2026-10-10T00:00:00Z",
    days: 21,
    requires: "memberMap",
    action: "map",
    copy: ({ native, dms }) => ({
      eyebrow: "New on BAM",
      title: dms ? "The coach map, and direct messages" : "The coach map is open",
      body: dms
        ? `See where every coach on BAM is coaching and find the ones near you. Tap any coach to see their profile and send them a message.${native ? "" : " Direct messages are on the web for now, and coming to the app soon."}`
        : "See where every coach on BAM is coaching and find the ones near you. Tap any coach to see their profile.",
      note: "Rather not be on the map? Turn off \"Show me on the community map\" in Settings.",
      primary: "Open the map",
    }),
  },
  {
    id: "film-studio-2026-10",
    startsAt: FILM_STUDIO_OPENS,
    days: 21,
    requires: "filmStudio",
    action: "film",
    // Film Studio isn't in the iPhone app.
    copy: ({ native }) => (native ? null : {
      eyebrow: "Now open",
      title: "BAM Film Studio is open",
      body: "Bring your film in, tag moments as you watch, add notes, voice notes and snapshots, and pull every clip back out when you need it.",
      primary: "Open Film Studio",
    }),
  },
];

/** The intro to show now, or null. Pure, for the tests. */
export function nextIntro({ now = Date.now(), joinedAt, seen = [], features = {}, native = false, dms = false, intros = FEATURE_INTROS } = {}) {
  const joined = joinedAt ? Date.parse(joinedAt) : null;
  for (const it of intros) {
    const start = Date.parse(it.startsAt);
    if (now < start || now > start + (it.days || 21) * 86400000) continue;
    if (seen.includes(it.id)) continue;
    if (it.requires && !features[it.requires]) continue;
    // Coaches who joined after it started meet it in the tour.
    if (joined && joined > start) continue;
    const copy = it.copy({ native, dms });
    if (copy) return { ...it, copy };
  }
  return null;
}
